import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentEditingField } from '../../store/selectors';
import { editField, setCurrentEditingField } from '../../store/slice';
import { getZIndex } from '../../utils';
import { NUMERIC_TYPES } from '../../../../../constants';
import { TextAlignment, WorksheetSchemaField } from '../../../../../types/custom-worksheet-v2';
import CWFieldInput from '../../../../../components/custom-worksheet/CWFieldInput';
import CWInputControlWrapper from '../../../../../components/custom-worksheet/CWInputControlWrapper';
import CWReadField from './CWReadField';

type CWFieldProps = {
    // Optional parent id which is only available if field is pinned on the parent header row
    parentId?: string;
    field: WorksheetSchemaField;
    mode?: 'row' | 'keyValue';
    alignment?: TextAlignment;
    allowRedirect?: boolean;
};

function CWField({ parentId, field, mode = 'keyValue', alignment, allowRedirect }: CWFieldProps): JSX.Element {
    const [value, setValue] = useState(field.value);

    const dispatch = useDispatch();
    const currentEditingField = useSelector(selectCurrentEditingField);

    const isEditing = currentEditingField === field.id;

    /**
     * Calculate z-index for the field input
     *
     * If it is a pinned field displayed on the same row as the parent property header,
     * z-index should be the same as the parent property header to prevent action buttons being hidden by the next property header
     */
    const zIndex = getZIndex(parentId || field.id);

    /**
     * If resetEditingField is true, setEditField(undefined) (this will clear bolded nav item and hide indigo side border)
     * If resetEditingField is false, don't setEditField(undefined) (this will allow sliding animation when moving from one nav item to another)
     */
    function handleSave(resetEditingField = true): void {
        let updatedFieldValue = value;

        if (updatedFieldValue === '' || updatedFieldValue === null) {
            updatedFieldValue = null;
        }

        if (NUMERIC_TYPES.includes(field.type) && (value === '.' || value === '-')) {
            if (resetEditingField) {
                dispatch(setCurrentEditingField(undefined));
            }
            return;
        }

        // If value entered is greater than the MAX_SAFE_INTEGER, don't save the value
        if (typeof value === 'number' && value > Number.MAX_SAFE_INTEGER) {
            return;
        }

        if (updatedFieldValue !== undefined) {
            dispatch(editField({ id: field.id, details: { value: updatedFieldValue } }));
        }

        if (resetEditingField) {
            dispatch(setCurrentEditingField(undefined));
        }
    }

    return (
        <div data-field="true" className="w-full">
            {isEditing ? (
                <CWInputControlWrapper
                    zIndex={zIndex}
                    onSave={handleSave}
                    onCancel={(): void => {
                        dispatch(setCurrentEditingField(undefined));
                    }}
                >
                    <CWFieldInput
                        fieldType={field.type}
                        onValueChange={setValue}
                        defaultValue={field.value}
                        showIcon={mode === 'keyValue'}
                    />
                </CWInputControlWrapper>
            ) : (
                <CWReadField
                    field={field}
                    showIcon={mode === 'keyValue'}
                    alignment={mode === 'keyValue' ? 'right' : alignment}
                    mode={mode}
                    allowRedirect={allowRedirect}
                />
            )}
        </div>
    );
}

export default CWField;
